import { type FC, useCallback, useMemo, useRef } from 'react';
import * as THREE from 'three';
import { type ImageProps } from '../../types';
import { ViewerLayer } from '../ViewerLayer';
import { cameraEulerFromAbyssRotation } from '../Util';
import { RENDER_ORDER_IMAGE_VIEWER } from '../RenderOrder';
import { useTextureLoader } from '../Loaders';
import type { ThreeEvent } from '@react-three/fiber';
import { useOnClick } from '../useOnClick';

export interface RectilinearImageExtendedProps extends ImageProps {
  opacity?: number;

  onHover?: (isHovered: boolean) => void;
  onClick?: () => void;
}

export const RectilinearImage: FC<RectilinearImageExtendedProps> = (image) => {
  const {
    url,
    previewUrl,
    requestHeader,
    position: positionProps,
    rotation: rotationProps,
    distance = 50,
    width,
    height,
    focalLength,
    cx,
    cy,
    opacity = 1.0,
    onHover,
    onClick,
  } = image;
  const { texture } = useTextureLoader({ url: previewUrl ?? url, requestHeader });

  const updatedTexture = useMemo(() => {
    if (texture) {
      // more crispy image with LinearFilter and without mipmaps
      // texture.generateMipmaps = false;
      texture.minFilter = THREE.LinearFilter;
      // texture.wrapS = THREE.RepeatWrapping;
      // texture.repeat.x = -1;
      texture.needsUpdate = true;
    }
    return texture;
  }, [texture]);

  const pixelsToMeters = useMemo(() => {
    return distance / focalLength;
  }, [distance, focalLength]);

  const meshReference = useRef<THREE.Mesh | null>(null);

  const handleMesh = useCallback((mesh: THREE.Mesh | null) => {
    mesh?.layers.enable(ViewerLayer.AutoPickable);
    meshReference.current = mesh;
  }, []);

  const rotation = useMemo(() => {
    return cameraEulerFromAbyssRotation(rotationProps);
  }, [rotationProps]);

  const position = useMemo(() => {
    const cameraPosition = new THREE.Vector3(...positionProps);
    const derectionVec = new THREE.Vector3(0, 0, -distance);
    derectionVec.applyEuler(rotation);
    return cameraPosition.add(derectionVec);
  }, [positionProps, rotation, distance]);

  const layers = useMemo(() => {
    const layers0 = new THREE.Layers();
    if (opacity > 0.0001) {
      layers0.set(ViewerLayer.SphericalViewerLayer);
    }
    return layers0;
  }, [opacity]);

  const shouldHandle = useCallback((event: ThreeEvent<MouseEvent>) => {
    const { altKey, ctrlKey, metaKey, shiftKey, button } = event;
    const isLeftButton = button === 0;
    if (!isLeftButton || altKey || ctrlKey || metaKey || shiftKey) {
      return false;
    }
    return true;
  }, []);

  const handleClick = useCallback(
    (event: ThreeEvent<MouseEvent>) => {
      if (onClick) {
        event.stopPropagation();
        onClick();
      }
    },
    [onClick]
  );

  const onPointerOver = useCallback(
    (event: ThreeEvent<MouseEvent>) => {
      if (onHover && event.intersections[0].object === meshReference.current) {
        onHover(true);
        event.stopPropagation();
      }
    },
    [onHover]
  );

  const onPointerOut = useCallback(() => {
    if (onHover) {
      onHover(false);
    }
  }, [onHover]);

  const { onPointerDown, onPointerUp } = useOnClick({ onClick: handleClick, shouldHandle });

  return (
    <>
      <group position={position} rotation={rotation} scale={[pixelsToMeters, pixelsToMeters, 1]}>
        <mesh
          ref={handleMesh}
          layers={layers}
          position={[-cx, cy, 0]}
          renderOrder={RENDER_ORDER_IMAGE_VIEWER - 1}
          onPointerDown={onPointerDown}
          onPointerUp={onPointerUp}
          onPointerOver={onPointerOver}
          onPointerOut={onPointerOut}
        >
          <planeGeometry args={[width, height]} />
          <meshBasicMaterial
            attach="material"
            opacity={opacity}
            map={updatedTexture}
            transparent
            depthWrite={false}
          />
        </mesh>
      </group>
    </>
  );
};
